import {html, type PropertyValues, type TemplateResult} from "lit";
import {customElement, property, query} from "lit/decorators.js";
import {FeatureDetector} from "../../common/featureDetector";
import {resolve} from "../../container";
import {KEYS, prepareEvents} from "../../common/utils/events";
import {fresh, ManagingResources} from "../../common/lifetime";
import {trackingButtonInteract} from "../../tracking/trackingAttributes";
import {UnLitElement} from "../../common/elements";
import {PopoverService} from "./popover/popoverService";

@customElement("eop-i-button")
export class EopIButton extends ManagingResources(UnLitElement) {
    @query(".i-button")
    public iButtonElement: HTMLButtonElement;
    @property({attribute: "i-button-style"})
    public iButtonStyle: string;
    @property()
    public label: string;
    @property({attribute: "popover-id"})
    private popoverId: string;

    public constructor(
        private features: FeatureDetector = resolve(FeatureDetector),
        private popoverService: PopoverService = resolve(PopoverService)) {
        super();
    }

    /**
     * type button is used to prevent form submission when the button is inside a form
     */
    public render(): TemplateResult {
        return html`
            <div class="i-button-container">
                ${this.label}
                <button
                        type="button"
                        class="i-button ${this.iButtonStyle}"
                        aria-label="i-button"
                        ${trackingButtonInteract("i-button-toggle")}
                ></button>
            </div>
        `;
    }

    public disconnectedCallback(): void {
        this.popoverService.unregister(this.popoverId);
        super.disconnectedCallback();
    }

    protected updated(_changedProperties: PropertyValues): void {
        super.updated(_changedProperties);

        this.popoverService.register(this.popoverId, this.iButtonElement);

        const lifetime = fresh(this, "firstUpdated");
        if (this.features.isTouchDevice()) {
            prepareEvents(this)
                .boundTo(lifetime)
                .on("click", () => {
                    this.popoverService.toggle(this.popoverId);
                });
        } else {
            prepareEvents(this)
                .boundTo(lifetime)
                .on("mouseenter", () => {
                    this.popoverService.open(this.popoverId);
                })
                .on("mouseleave", () => this.popoverService.close(this.popoverId));
        }

        prepareEvents(this.iButtonElement)
            .boundTo(lifetime)
            .on("keydown", (event: Event) => {
                const keyEvent = event as KeyboardEvent;
                if (keyEvent.key === KEYS.ENTER || keyEvent.key === KEYS.SPACE) {
                    this.popoverService.toggle(this.popoverId);
                } else if (keyEvent.key === KEYS.ESCAPE) {
                    this.popoverService.close(this.popoverId);
                    this.iButtonElement.focus();
                } else if (keyEvent.key === KEYS.TAB) {
                    this.popoverService.close(this.popoverId);
                }
            });

    }
}